/**
 * Phase 8.7 — non-interrupting flash-toast banner for foreground events.
 *
 * Currently wired to two events:
 *
 *   `notification.toast` (kind: `duel_challenge`) — fires when an online
 *   user receives a duel-challenge invite. The brief is explicit: this
 *   slides in over the current screen, NEVER blocks input (no modal),
 *   auto-dismisses after ~7s, and offers a quick Accept inline.
 *
 *   `notification.toast` (kind: `match_ready`) — the inverse: fires for
 *   the *challenger* when their opponent accepts the duel. Shows a slim
 *   gold-accented banner with a "Play now" button that deep-links
 *   straight into the match (no extra API call — the match was minted
 *   server-side when the opponent accepted).
 *
 * Architecture choices:
 *
 *   * One global toast host element, created lazily and parented to
 *     <body> so SPA navigation doesn't sweep it away.
 *   * Only ONE flash toast at a time. A new event replaces the previous
 *     one (rather than stacking) — if a player is being challenged by
 *     three people in rapid succession the latest is the most actionable.
 *   * The host element uses `pointer-events: none` everywhere except on
 *     the buttons, so mid-game taps anywhere outside the chip still hit
 *     the underlying screen — that's the "never blocks" guarantee.
 *   * Started by `ensureNotificationsStarted()` so it shares the same
 *     bus subscription lifecycle as the bell badge.
 */

import { api } from '../api.js';
import { I, icon } from '../components/icons.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { onNotifications } from './notifications.js';
import { navigate } from '../router.js';

const AUTO_DISMISS_MS = 7_000;
const HOST_ID = 'triviana-flash-toast-host';

let _hostEl = null;
let _activeEl = null;
let _hideTimer = null;
let _started = false;
let _unsub = null;

function _ensureHost() {
  if (_hostEl) return _hostEl;
  _hostEl = document.createElement('div');
  _hostEl.id = HOST_ID;
  // Pointer-events:none on the wrapper means the area outside the toast
  // chip stays fully tap-through. The chip itself flips it back on.
  Object.assign(_hostEl.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    zIndex: '9000',
    padding: 'max(env(safe-area-inset-top), 12px) 14px 0',
    pointerEvents: 'none',
  });
  document.body.appendChild(_hostEl);
  return _hostEl;
}

function _clearActive() {
  if (_hideTimer) {
    clearTimeout(_hideTimer);
    _hideTimer = null;
  }
  if (_activeEl) {
    _activeEl.remove();
    _activeEl = null;
  }
}

function _scheduleAutoDismiss() {
  if (_hideTimer) clearTimeout(_hideTimer);
  _hideTimer = setTimeout(() => {
    if (!_activeEl) return;
    _activeEl.style.transition = 'opacity 220ms, transform 220ms';
    _activeEl.style.opacity = '0';
    _activeEl.style.transform = 'translateY(-8px)';
    setTimeout(_clearActive, 240);
  }, AUTO_DISMISS_MS);
}

function _renderDuelChallenge(payload) {
  const host = _ensureHost();
  _clearActive();

  const from = escape(payload.from_username || 'A friend');
  const stake = Number(payload.stake_kobo || 0);
  const categoryName = (payload.category_name || '').trim();
  // Subline order matches the inbox/push body so the same challenge
  // reads identically on every surface: "<Topic> · Staked ₦X" or just
  // "Staked ₦X" / "<Topic>" / "" depending on what's set.
  const subBits = [];
  if (categoryName) subBits.push(categoryName);
  if (stake > 0) subBits.push(`Staked ₦${(stake / 100).toLocaleString()}`);
  subBits.push('now · auto-hides');
  const sub = subBits.join(' · ');
  const challengeId = payload.challenge_id;
  const deeplink = payload.deeplink || '/friends?tab=challenges';

  const el = document.createElement('div');
  el.className = 'flash-toast';
  // Inline styles mirror the wireframe (screen 4's duel-challenge toast)
  // so we don't depend on a CSS class landing in a separate change.
  Object.assign(el.style, {
    pointerEvents: 'auto',
    background: '#15323A',
    border: '1px solid #1E6E5C',
    borderRadius: '14px',
    padding: '11px 13px',
    boxShadow: '0 12px 26px rgba(0,0,0,0.45)',
    position: 'relative',
    transform: 'translateY(-12px)',
    opacity: '0',
    transition: 'opacity 220ms, transform 220ms',
  });
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.innerHTML = `
    <span style="position:absolute;top:11px;right:13px;font-size:9px;
                 color:#7FC3B2;font-weight:600;">${escape(sub.split(' · ').pop())}</span>
    <div class="row">
      <div class="ic" style="background:#0F352A;color:#5FE0BE;">
        ${icon(I.swords, { size: 17 })}
      </div>
      <div class="grow" style="min-width:0;">
        <b style="font-size:13px;color:#fff;font-weight:600;display:block;">
          ${from} challenged you to a duel
        </b>
        <div style="font-size:11px;color:#9FE9D2;font-weight:500;margin-top:1px;">
          ${escape(sub.split(' · now')[0]) || ' '}
        </div>
      </div>
    </div>
    <div class="row" style="gap:8px;margin-top:9px;">
      <button type="button" data-flash-action="accept"
              class="btn teal sm"
              style="flex:1;width:auto;margin:0;">Accept</button>
      <button type="button" data-flash-action="dismiss"
              class="btn ghost sm"
              style="flex:1;width:auto;margin:0;">Dismiss</button>
    </div>
  `;

  host.appendChild(el);
  _activeEl = el;
  // Defer the slide-in by one frame so the CSS transition kicks in.
  requestAnimationFrame(() => {
    el.style.opacity = '1';
    el.style.transform = 'translateY(0)';
  });

  el.querySelector('[data-flash-action="dismiss"]').addEventListener(
    'click',
    () => {
      _clearActive();
    },
  );
  el.querySelector('[data-flash-action="accept"]').addEventListener(
    'click',
    async () => {
      _clearActive();
      if (!challengeId) {
        navigate(deeplink);
        return;
      }
      try {
        const ch = await api(`/challenges/${encodeURIComponent(challengeId)}/accept`, {
          method: 'POST',
        });
        if (ch?.match_id) {
          navigate(`/match/${ch.match_id}`);
        } else {
          navigate(deeplink);
        }
      } catch (err) {
        toast.fromError(err, 'Could not accept the duel.');
        navigate(deeplink);
      }
    },
  );

  _scheduleAutoDismiss();
}

function _handleEvent(event) {
  if (!event || event.kind !== 'toast') return;
  if (event.toast_kind === 'duel_challenge') {
    _renderDuelChallenge(event);
    return;
  }
  if (event.toast_kind === 'match_ready') {
    _renderMatchReady(event);
    return;
  }
  // Unknown toast_kind — silently ignore so the server can roll out new
  // kinds before clients learn about them.
}


function _renderMatchReady(payload) {
  const host = _ensureHost();
  _clearActive();

  const from = escape(payload.from_username || 'Your opponent');
  const matchId = payload.match_id || '';
  const deeplink = payload.deeplink || (matchId ? `/match/${matchId}` : '/');

  const el = document.createElement('div');
  el.className = 'flash-toast';
  // Gold gradient + tinted border signal urgency (your opponent is in,
  // play now) — distinct from the teal duel_challenge palette so the
  // two events don't blur visually.
  Object.assign(el.style, {
    pointerEvents: 'auto',
    background: 'linear-gradient(135deg, #2A2418, #1F1A12)',
    border: '1px solid #5A4B17',
    borderRadius: '14px',
    padding: '11px 13px',
    boxShadow: '0 12px 26px rgba(0,0,0,0.45)',
    position: 'relative',
    transform: 'translateY(-12px)',
    opacity: '0',
    transition: 'opacity 220ms, transform 220ms',
  });
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.innerHTML = `
    <span style="position:absolute;top:11px;right:13px;font-size:9px;
                 color:#E0B453;font-weight:600;">auto-hides</span>
    <div class="row">
      <div class="ic" style="background:#3A2E12;color:#F7C667;">
        ${icon(I.swords, { size: 17 })}
      </div>
      <div class="grow" style="min-width:0;">
        <b style="font-size:13px;color:#fff;font-weight:600;display:block;">
          ${from} accepted your duel
        </b>
        <div style="font-size:11px;color:#E5C778;font-weight:500;margin-top:1px;">
          Let’s play
        </div>
      </div>
    </div>
    <div class="row" style="gap:8px;margin-top:9px;">
      <button type="button" data-flash-action="play"
              class="btn gold sm"
              style="flex:1;width:auto;margin:0;">Play now</button>
      <button type="button" data-flash-action="dismiss"
              class="btn ghost sm"
              style="flex:1;width:auto;margin:0;">Later</button>
    </div>
  `;

  host.appendChild(el);
  _activeEl = el;
  requestAnimationFrame(() => {
    el.style.opacity = '1';
    el.style.transform = 'translateY(0)';
  });

  el.querySelector('[data-flash-action="dismiss"]').addEventListener('click', () => {
    _clearActive();
  });
  el.querySelector('[data-flash-action="play"]').addEventListener('click', () => {
    _clearActive();
    navigate(deeplink);
  });

  _scheduleAutoDismiss();
}

/** Idempotent. Subscribes to the notifications bus and renders flash
 *  toasts for `notification.toast` events. Called from
 *  `ensureNotificationsStarted()`. */
export function startFlashToast() {
  if (_started) return;
  _started = true;
  _unsub = onNotifications(_handleEvent);
}

export function stopFlashToast() {
  if (_unsub) {
    _unsub();
    _unsub = null;
  }
  _clearActive();
  _started = false;
}
